<template>
    <div id="billing-review-details">
        <div v-if="loading" class="text-center text-danger my-3">
            <b-spinner class="align-middle" />
            <strong class="ml-2">Loading...</strong>
        </div>

        <div v-else-if="detail.fill.storeID">
            <b-row>
                <b-col cols="8">
                    <h5 class="mb-1">
                        <router-link :to="prescriptionRoute" target="_blank">
                            {{ detail.fill.rxId }} <small><i class="fas fa-external-link-alt" /></small>
                        </router-link>
                    </h5>
                    <div>{{ detail.fill.patientName }} &mdash; {{ detail.fill.drugName }}</div>
                    <div class="text-muted">
                        <small>
                            {{ detail.fill.storeName }}
                            <span v-if="detail.fill.programName"> &middot; {{ detail.fill.programName }}</span>
                            <span v-if="detail.fill.fillDate"> &middot; Filled {{ formatDate(detail.fill.fillDate) }}</span>
                        </small>
                    </div>
                </b-col>
                <b-col cols="4" class="text-right">
                    <h5 v-if="detail.fill.isOutstanding" class="text-danger mb-1">
                        {{ detail.fill.billingReviewReason }}
                    </h5>
                    <h5 v-else class="text-success mb-1">Complete</h5>
                    <div v-if="!blocksOrderCreation" class="text-muted">
                        <small>Does not hold the fill</small>
                    </div>
                </b-col>
            </b-row>

            <hr>

            <b-table id="billing-review-detail-table" outlined small show-empty
                empty-text="No claims were scheduled for this fill"
                :fields="detailFields" :items="detail.claims" :tbody-tr-class="rowClass">
                <template #cell(sequencePosition)="data">
                    {{ data.value }}
                </template>

                <template #cell(expected)="data">
                    <strong>{{ data.item.expectedOutcomeLabel }}</strong>
                    <div v-if="data.item.expectedRejectCodeList.length" class="text-muted">
                        <small>{{ data.item.expectedRejectCodeList.join(", ") }}</small>
                    </div>
                </template>

                <template #cell(actual)="data">
                    <span v-if="!data.item.hasOutcome" class="text-muted">
                        {{ statusLabel(data.item.status) }}
                    </span>
                    <template v-else>
                        <strong :class="statusClass(data.item.actualTransactionStatus)">
                            {{ data.item.actualTransactionStatus }}
                        </strong>
                        <div v-if="data.item.actualRejectCodeList.length" class="text-muted">
                            <small>{{ rejectCodeSummary(data.item) }}</small>
                        </div>
                        <div v-if="data.item.reversed" class="text-muted"><small>Reversed</small></div>
                    </template>
                </template>

                <template #cell(planPaid)="data">
                    <span v-if="data.item.planPaid !== null">{{ data.item.planPaid | currency }}</span>
                </template>

                <template #cell(actualCopay)="data">
                    <span v-if="data.item.actualCopay !== null">{{ data.item.actualCopay | currency }}</span>
                </template>
            </b-table>

            <div class="d-flex justify-content-between align-items-center">
                <router-link :to="prescriptionRoute" target="_blank">
                    Open the prescription to correct the billing
                    <small><i class="fas fa-external-link-alt" /></small>
                </router-link>
                <b-button v-if="detail.fill.isOutstanding" variant="success" :disabled="completing"
                    @click="completeClicked">
                    Complete Review
                </b-button>
            </div>
        </div>

        <div v-else class="text-muted">Prescription not found.</div>
    </div>
</template>

<script lang="ts">
import Axios from "axios";
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import moment from "moment";
import { BvTableFieldArray } from "bootstrap-vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import {
    BillingReviewDetail,
    BillingReviewDetailItem,
    BillingReviewReason,
} from "@/models/BillingReview";
import { ScheduledClaimStatus } from "@/models/ScheduledClaim";

/**
 * Scheduled intent beside actual outcome for one fill. Read-only apart from the Complete button:
 * remediation happens on the prescription, whose billing panel runs through the save orchestrator,
 * so rebuilding any part of it here would be a second path to the same money.
 */
@Component({ name: "BillingReviewDetails" })
export default class BillingReviewDetails extends Vue {
    @Prop({ required: true }) storeId!: number;
    @Prop({ required: true }) rxNumber!: number;
    @Prop({ required: true }) rfNumber!: number;

    private detail: BillingReviewDetail = new BillingReviewDetail();
    private loading = false;
    private completing = false;

    get detailFields(): BvTableFieldArray {
        return [
            { key: "sequencePosition", label: "#" },
            { key: "planName", label: "Plan" },
            { key: "expected", label: "Expected" },
            { key: "actual", label: "Actual" },
            { key: "planPaid", label: "Plan Paid" },
            { key: "actualCopay", label: "Copay" },
        ];
    }

    get prescriptionRoute() {
        return {
            name: "PrescriptionDetails",
            params: {
                storeID: this.storeId,
                rxNumber: this.rxNumber,
                rfNumber: this.rfNumber,
            },
        };
    }

    get blocksOrderCreation(): boolean {
        return this.detail.fill.billingReviewReason !== BillingReviewReason.AuditSample;
    }

    formatDate(value: string | null): string {
        return value ? moment(value).format("L") : "";
    }

    statusLabel(status: ScheduledClaimStatus): string {
        return ScheduledClaimStatus[status] ?? "";
    }

    statusClass(transactionStatus: string | null): string {
        const status = (transactionStatus ?? "").trim().toLowerCase();
        if (status === "rejected") return "text-danger";
        if (status === "paid") return "text-success";
        return "";
    }

    // Highlights only the plan that broke the prediction, so a long sequence still points at one row.
    rowClass(item: BillingReviewDetailItem | null): string {
        return item?.isMismatch ? "table-warning" : "";
    }

    rejectCodeSummary(item: BillingReviewDetailItem): string {
        const returned = item.actualRejectCodeList.join(", ");
        if (item.rejectCodesSatisfied) return returned;

        return `${returned} (expected ${item.expectedRejectCodeList.join(", ")})`;
    }

    created() {
        this.load();
    }

    @Watch("storeId")
    @Watch("rxNumber")
    @Watch("rfNumber")
    onFillChanged() {
        this.load();
    }

    async load() {
        if (!this.storeId) return;
        try {
            this.loading = true;
            const resp = await Axios.get(
                `/BillingReview/${this.storeId}/${this.rxNumber}/${this.rfNumber}`);
            this.detail = new BillingReviewDetail(resp.data);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.loading = false;
        }
    }

    async completeClicked() {
        try {
            this.completing = true;
            await Axios.post(
                `/BillingReview/${this.storeId}/${this.rxNumber}/${this.rfNumber}/Complete`);
            this.$notification(NotificationOptions.successSaveNotificationPreset("Billing Review"));
            this.$emit("completed", this.detail.fill.rxId);
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.completing = false;
        }
    }
}
</script>

<style scoped></style>
